import { useCallback, useEffect, useState, type JSX } from 'react';
import { Loader2, Send, Megaphone, Star } from 'lucide-react';
import type { QuotaPolicy } from '@cca/xero-shared';
import { api, type AdminConfig, type AdminParticipant, type ExclusionCategory } from './api/client';
import { PolicyForm } from '@cca/quota-ui';
import { defaultPolicy } from './pages/policyDefaults';

export function AdminPage(): JSX.Element {
  const [cfg, setCfg] = useState<AdminConfig | null>(null);
  const [participants, setParticipants] = useState<AdminParticipant[]>([]);
  const [categories, setCategories] = useState<ExclusionCategory[]>([]);
  const [year, setYear] = useState(2026);
  const [policy, setPolicy] = useState<QuotaPolicy>(() => defaultPolicy(2026));
  const [label, setLabel] = useState('');
  const [pubName, setPubName] = useState('');
  const [pubNote, setPubNote] = useState('');
  const [subject, setSubject] = useState('CCAANZ Quota Model — an update');
  const [message, setMessage] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const [config, parts, cats] = await Promise.all([
      api.adminConfig(),
      api.adminParticipants().then((r) => r.participants),
      api.categories().then((r) => r.categories),
    ]);
    setCfg(config);
    setParticipants(parts);
    setCategories(cats);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const flash = (m: string): void => {
    setMsg(m);
    setTimeout(() => setMsg(''), 4000);
  };
  const errMsg = (e: unknown, what: string): string =>
    e instanceof Error ? `${what} failed: ${e.message}` : `${what} failed. Please try again.`;

  const setOpen = async (isOpen: boolean): Promise<void> => {
    setBusy(true);
    try {
      await api.adminSetConfig({ isOpen });
      await reload();
      flash(isOpen ? 'Model opened.' : 'Model closed.');
    } catch (e) {
      flash(errMsg(e, isOpen ? 'Open' : 'Close'));
    } finally {
      setBusy(false);
    }
  };
  const saveBaseline = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.adminSetConfig({
        baseline: { policy, assessment_year: year, label: label.trim() },
      });
      await reload();
      flash('Baseline set — participants now compare against it.');
    } catch (e) {
      flash(errMsg(e, 'Set baseline'));
    } finally {
      setBusy(false);
    }
  };
  const publish = async (): Promise<void> => {
    if (!pubName.trim()) return;
    setBusy(true);
    try {
      await api.adminPublish(pubName.trim(), pubNote.trim(), policy);
      setPubName('');
      setPubNote('');
      flash('Published — visible to all participants.');
    } catch (e) {
      flash(errMsg(e, 'Publish'));
    } finally {
      setBusy(false);
    }
  };
  const broadcast = async (): Promise<void> => {
    if (!message.trim()) return;
    setBusy(true);
    try {
      const r = await api.adminBroadcast(subject.trim(), message.trim());
      flash(`Emailed ${r.sent} of ${r.recipients} participants.`);
      setMessage('');
    } catch (e) {
      flash(errMsg(e, 'Broadcast'));
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return <p style={{ color: '#6b7280' }}>Loading admin…</p>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Fixed toast: the admin actions live well below the fold, so an
          in-flow banner at the top of the page was invisible when clicked. */}
      {msg && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 60,
            background: '#1a3a5c',
            color: '#fff',
            padding: '10px 18px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            maxWidth: 380,
          }}
        >
          {msg}
        </div>
      )}

      {/* Window control */}
      <section style={panel}>
        <h2 style={h2}>Model window</h2>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            style={{
              fontWeight: 700,
              color: cfg.isOpen ? '#2e7d32' : '#c62828',
              fontSize: 15,
            }}
          >
            {cfg.isOpen ? '● Open' : '● Closed'}
          </span>
          <button
            style={cfg.isOpen ? btnDanger : btnPrimary}
            disabled={busy}
            onClick={() => void setOpen(!cfg.isOpen)}
          >
            {cfg.isOpen ? 'Close model' : 'Open model'}
          </button>
          <span style={hint}>
            Data as at: {cfg.dataAsAt ? new Date(cfg.dataAsAt).toLocaleString('en-NZ') : '—'}
            {cfg.churchCount != null && ` · ${cfg.churchCount} churches`}
          </span>
        </div>
      </section>

      {/* Participants */}
      <section style={panel}>
        <h2 style={h2}>Participants ({participants.length})</h2>
        {participants.length === 0 && <p style={hint}>No one has signed in yet.</p>}
        {participants.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr
                style={{ textAlign: 'left', color: '#5c4f33', borderBottom: '2px solid #e5e0d5' }}
              >
                <th style={th}>Name</th>
                <th style={th}>Church</th>
                <th style={th}>Email</th>
                <th style={thR}>Policies</th>
                <th style={thR}>Last active</th>
                <th style={thR}>In Zoho</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((p) => (
                <tr key={p.id} style={{ borderBottom: '1px solid #f0ebde' }}>
                  <td style={td}>{p.display_name ?? '—'}</td>
                  <td style={td}>{p.church ?? '—'}</td>
                  <td style={td}>{p.email}</td>
                  <td style={tdR}>{p.policy_count}</td>
                  <td style={tdR}>
                    {p.last_activity ? new Date(p.last_activity).toLocaleDateString('en-NZ') : '—'}
                  </td>
                  <td style={tdR}>{p.zoho_synced_at ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Baseline + publish — shape a policy, then set it as the baseline or publish it */}
      <section style={panel}>
        <h2 style={h2}>Shape a policy → set as baseline or publish</h2>
        <p style={hint}>
          The <strong>baseline</strong> is what every participant compares against.{' '}
          <strong>Published</strong> policies appear in their “Registrar’s preferred” list
          (read-only).
        </p>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            margin: '12px 0',
            flexWrap: 'wrap',
          }}
        >
          <label style={hint}>
            Invoice year{' '}
            <input
              type="number"
              style={{ ...input, width: 90, display: 'inline-block', marginBottom: 0 }}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </label>
          <input
            style={{ ...input, flex: 1, marginBottom: 0, minWidth: 180 }}
            placeholder="Baseline label (e.g. Proposed 2026 policy)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <button style={btnPrimary} disabled={busy} onClick={() => void saveBaseline()}>
            <Star size={14} /> Set as baseline
          </button>
          <span style={hint}>
            Current baseline:{' '}
            {cfg.hasBaseline
              ? `${cfg.baselineLabel?.trim() ? `“${cfg.baselineLabel}”` : 'unlabelled'}${
                  cfg.updatedAt
                    ? ` · config saved ${new Date(cfg.updatedAt).toLocaleString('en-NZ', {
                        day: 'numeric',
                        month: 'short',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}`
                    : ''
                }`
              : 'none set'}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          <input
            style={{ ...input, flex: 1, marginBottom: 0, minWidth: 160 }}
            placeholder="Publish name"
            value={pubName}
            onChange={(e) => setPubName(e.target.value)}
          />
          <input
            style={{ ...input, flex: 2, marginBottom: 0, minWidth: 160 }}
            placeholder="Note (optional)"
            value={pubNote}
            onChange={(e) => setPubNote(e.target.value)}
          />
          <button
            style={btnGhost}
            disabled={busy || !pubName.trim()}
            onClick={() => void publish()}
          >
            <Send size={14} /> Publish as preferred
          </button>
        </div>
        <PolicyForm policy={policy} onChange={setPolicy} categories={categories} hideVersion />
      </section>

      {/* Broadcast */}
      <section style={panel}>
        <h2 style={h2}>
          <Megaphone size={15} style={{ verticalAlign: -2 }} /> Email all participants
        </h2>
        <input
          style={input}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
        />
        <textarea
          style={{ ...input, minHeight: 90, resize: 'vertical' }}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Message — e.g. the preferred policies are now published; please take a look and compare."
        />
        <button
          style={btnPrimary}
          disabled={busy || !message.trim()}
          onClick={() => void broadcast()}
        >
          {busy ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Send to{' '}
          {participants.length}
        </button>
      </section>
    </div>
  );
}

const panel: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e0d5',
  borderRadius: 8,
  padding: 16,
};
const h2: React.CSSProperties = {
  fontSize: 14,
  color: '#005487',
  marginBottom: 10,
  fontWeight: 600,
};
const hint: React.CSSProperties = { fontSize: 12, color: '#6b7280' };
const input: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 10px',
  marginBottom: 10,
  border: '1px solid #d8d2c4',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};
const btnBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const btnPrimary: React.CSSProperties = { ...btnBase, background: '#85754d', color: '#fff' };
const btnDanger: React.CSSProperties = { ...btnBase, background: '#c62828', color: '#fff' };
const btnGhost: React.CSSProperties = {
  ...btnBase,
  background: '#fff',
  color: '#005487',
  border: '1px solid #c7d2e0',
};
const th: React.CSSProperties = { padding: '6px 8px', fontWeight: 600 };
const thR: React.CSSProperties = { ...th, textAlign: 'right' };
const td: React.CSSProperties = { padding: '6px 8px' };
const tdR: React.CSSProperties = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
